import {Component, Inject, ViewEncapsulation} from '@angular/core';
import {CartService} from '../../../shared/services/cart.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {DocumentType} from '../../../shared/models/user';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Person, PersonLinkType} from '../../../shared/models/person';
import {DialogsService, FormService, LangService, LoaderService, NavigateService} from 'ngx-satoris';
import {ApiService} from '../../../shared/services/api.service';
import {enumToArrayObject} from '../../../shared/utils/enumToArrayObject';
import {RequestService} from 'src/app/shared/services/request.service';
import {warnIfInvalidName} from 'src/app/shared/utils/form';

@Component({
  selector: 'app-dialog-new-person',
  templateUrl: './dialog-new-person.component.html',
  styleUrl: './dialog-new-person.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class DialogNewPersonComponent {

  form: FormGroup;
  submitted = false;
  linkTypeOptions;
  otherOptions = false;
  sexOptions = [{name: 'DYN.male', value: 'M'}, {name: 'DYN.female', value: 'F'}, {name: 'DYN.other', value: '?'}];
  countriesOptions = this.request.getNationalities(false);

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    public cart: CartService,
    public dialogRef: MatDialogRef<DialogNewPersonComponent>,
    public nav: NavigateService,
    public forms: FormService,
    public api: ApiService,
    private formBuilder: FormBuilder,
    private loader: LoaderService,
    private lang: LangService,
    private request: RequestService,
    private dialog: DialogsService) {
    this.linkTypeOptions = enumToArrayObject(PersonLinkType).filter((item) => +item.value !== PersonLinkType.UNKNOWN).map(item => ({
      name: 'personLinkType.DYN.' + item.value,
      value: item.value
    }));
    this.setForm();
  }

  setForm() {
    this.form = this.formBuilder.group({
      firstName: ['', [
        Validators.pattern('[A-Za-z\' -]+'),
        Validators.maxLength(50)
      ]],
      lastName: ['', [
        Validators.pattern('[A-Za-z\' -]+'),
        Validators.maxLength(50)
      ]],
      personalNr: ['', [
        Validators.pattern('[a-zA-Z0-9\-]+'),
        Validators.maxLength(20)
      ]],
      passportNr: ['', [
        Validators.pattern('^[A-Z0-9]+$'),
        Validators.required,
        Validators.maxLength(20)
      ]],
      birthDate: ['', []],
      sex: ['', [
        Validators.pattern('^[MF\\?]$')
      ]],
      birthPlaceCity: ['', [
        Validators.pattern('[A-Za-z\' -]+'),
        Validators.maxLength(50)
      ]],
      birthPlaceCountry: [''],
      linkType: ['', [Validators.required]],
      otherLinkType: ['', [
        Validators.pattern('[A-Za-z\' -]+'),
        Validators.required,
        Validators.maxLength(50)
      ]]
    });

    this.form.get('linkType').valueChanges.subscribe(value => {
      const otherLinkTypeControl = this.form.get('otherLinkType');
      if(+value === PersonLinkType.OTHER) {
        this.otherOptions = true;
        otherLinkTypeControl.setValidators([Validators.required, Validators.pattern('[A-Za-zàáâãäåçèéêëìíîïðòóôõöùúûüýÿñÀÁÂÃÄÅÇÈÉÊËÌÍÎÏÐÒÓÔÕÖÙÚÛÜÝŸÑ\'-\\s]+'), Validators.maxLength(50)]);
      } else {
        this.form.get('otherLinkType').setValue('');
        this.otherOptions = false;
        otherLinkTypeControl.setValidators([Validators.pattern('[A-Za-zàáâãäåçèéêëìíîïðòóôõöùúûüýÿñÀÁÂÃÄÅÇÈÉÊËÌÍÎÏÐÒÓÔÕÖÙÚÛÜÝŸÑ\'-\\s]+'), Validators.maxLength(50)]);
      }
      otherLinkTypeControl.updateValueAndValidity();
    });
  }

  submit() {
    const firstName = this.form.get('firstName').value?.trim();
    const lastName  = this.form.get('lastName').value?.trim();
    const hasAtLeastOneName = firstName || lastName;
    if(this.forms.getFormErrors(this.form).length > 0 || !hasAtLeastOneName) {
      this.submitted = true;
      return;
    }
    warnIfInvalidName(firstName, lastName, this.loader).then(canContinue => {
      if(!canContinue) {
        this.dialog.openDialog(DialogNewPersonComponent, 'md');
        return;
      }
      this.submitCreatePerson();
    });
  }

  private submitCreatePerson(): void {
    const isDuplicate = this.api.listPersons.find((person) => (person.firstName.toLowerCase() === this.form.get('firstName').value.toLowerCase() && person.lastName.toLowerCase() === this.form.get('lastName').value.toLowerCase()));
    this.loader.loading(true, {
      type: isDuplicate? 'warn' : 'question',
      message: this.lang.transform(isDuplicate ? 'createPeopleDuplicate.ask.DYN.' : 'createPeople.ask.DYN.'),
      btnLabel: 'global.confirm'
    }).then((res: boolean) => {
      if(res) {
        this.loader.loading(true);
        this.api.createPerson(this.form.get('personalNr').value,
          this.form.get('firstName').value || '',
          this.form.get('lastName').value || '',
          this.form.get('passportNr').value,
          +this.form.get('linkType').value,
          this.form.get('otherLinkType').value,
          new Date(this.form.get('birthDate').value),
          this.form.get('sex').value,
          this.form.get('birthPlaceCity').value,
          this.form.get('birthPlaceCountry').value
        ).then(() => {
          this.api.persons().then((res: Person[]) => {
            this.loader.loading(false);
            this.loader.loading(true, {
              type: 'valid',
              message: this.lang.transform('createPeople.success.DYN.')
            }).then(() => {
              this.api.listPersons = this.request.getPersonsWithReviewStatus(res);
              this.closeDialog();
            });
          });
        }).catch((err) => {
          this.loader.loading(true, {type: 'error', message: this.lang.transform(err || 'createPeople.error')});
        });
      }
    });
  }

  closeDialog(done?: boolean) {
    this.dialogRef?.close(done);
  }
  protected readonly DocumentType = DocumentType;
}
